'use client';

// LES FORMULES — quotas réels du cahier §15.2, affichés sans détour.
//
// Volontairement STATIQUE (pas de fetch de /api/subscriptions/plans) : sur une landing, un
// tableau de prix doit s'afficher instantanément, sans état de chargement ni décalage de mise
// en page, et rester lisible par les moteurs de recherche. La page /tarifs, elle, reste la
// source vivante branchée sur l'API.

import Link from 'next/link';
import { ArrowRight, Check, Sparkles } from 'lucide-react';

import { useT } from '@/hooks/useT';
import { useReveal } from '@/hooks/useReveal';
import { fontStyle } from '@/theme/typography';

// `price` : null = gratuit. `rows` = 4 lignes de quotas (souvenirs, invités, moments, stockage).
const PLANS = [
  { key: 'discovery', price: null, featured: false },
  { key: 'family', price: '5,99', featured: false },
  { key: 'plus', price: '9,99', featured: true },
  { key: 'max', price: '19,99', featured: false },
] as const;

const ROWS = ['memories', 'guests', 'moments', 'storage'] as const;

export function Plans() {
  const { t } = useT('landing');
  const headRef = useReveal<HTMLDivElement>();

  return (
    <section
      id="plans"
      className="relative mx-auto w-full max-w-6xl px-5 py-20 sm:px-8 lg:py-28"
    >
      <div
        ref={headRef}
        className="flex flex-col items-center gap-4 text-center"
      >
        <span
          className="rmbr-rise uppercase tracking-[0.18em] text-brand"
          style={fontStyle('caption')}
        >
          {t('plans.eyebrow')}
        </span>
        <h2
          className="rmbr-rise max-w-2xl text-balance text-text-primary"
          style={{
            ['--rmbr-stagger' as string]: '80ms',
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4.2vw, 3.25rem)',
            lineHeight: 1.04,
            letterSpacing: '-0.03em',
            fontWeight: 600,
          }}
        >
          {t('plans.heading')}
        </h2>
        <p
          className="rmbr-rise max-w-xl text-pretty text-text-secondary"
          style={{
            ...fontStyle('bodyLg'),
            ['--rmbr-stagger' as string]: '160ms',
          }}
        >
          {t('plans.subtitle')}
        </p>
      </div>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {PLANS.map((plan, i) => (
          <PlanCard key={plan.key} plan={plan} index={i} />
        ))}
      </div>

      <p
        className="mt-8 text-center text-text-tertiary"
        style={fontStyle('bodySm')}
      >
        {t('plans.footnote')}
      </p>
    </section>
  );
}

function PlanCard({
  plan,
  index,
}: {
  plan: { key: string; price: string | null; featured: boolean };
  index: number;
}) {
  const { t } = useT('landing');
  const ref = useReveal<HTMLElement>({ threshold: 0.1 });
  const { key, price, featured } = plan;

  return (
    <article
      ref={ref}
      className={`rmbr-rise rmbr-card-hover relative flex flex-col gap-5 rounded-xl border p-6 ${
        featured
          ? 'border-brand/50 bg-surface shadow-[var(--rmbr-shadow-xl)]'
          : 'border-border bg-surface shadow-[var(--rmbr-shadow-card)]'
      }`}
      style={{ ['--rmbr-stagger' as string]: `${index * 80}ms` }}
    >
      {featured && (
        <span
          className="absolute -top-3 left-6 flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 text-brand-on"
          style={fontStyle('caption')}
        >
          <Sparkles className="size-3" />
          {t('plans.popular')}
        </span>
      )}

      <header className="flex flex-col gap-1">
        <h3
          className="text-text-primary"
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 21,
            fontWeight: 600,
            letterSpacing: '-0.01em',
          }}
        >
          {t(`plans.${key}.name`)}
        </h3>
        <span style={fontStyle('bodySm')} className="text-text-secondary">
          {t(`plans.${key}.pitch`)}
        </span>
      </header>

      <div className="flex items-baseline gap-1.5">
        {price === null ? (
          <span
            className="text-brand"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: '-0.02em',
            }}
          >
            {t('plans.free')}
          </span>
        ) : (
          <>
            <span
              className="text-text-primary"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 34,
                fontWeight: 600,
                letterSpacing: '-0.02em',
              }}
            >
              {price} €
            </span>
            <span style={fontStyle('bodySm')} className="text-text-tertiary">
              {t('plans.perMonth')}
            </span>
          </>
        )}
      </div>

      <ul className="flex flex-col gap-2.5 border-t border-border pt-5">
        {ROWS.map((row) => (
          <li
            key={row}
            className="flex items-start gap-2.5"
            style={fontStyle('bodySm')}
          >
            <Check
              className={`mt-0.5 size-3.5 shrink-0 ${featured ? 'text-brand' : 'text-text-tertiary'}`}
            />
            <span className="text-text-secondary">
              <span className="text-text-primary">
                {t(`plans.${key}.${row}`)}
              </span>{' '}
              {t(`plans.label.${row}`)}
            </span>
          </li>
        ))}
        <li className="flex items-start gap-2.5" style={fontStyle('bodySm')}>
          <Check
            className={`mt-0.5 size-3.5 shrink-0 ${featured ? 'text-brand' : 'text-text-tertiary'}`}
          />
          <span className="text-text-secondary">{t(`plans.${key}.roles`)}</span>
        </li>
      </ul>

      <Link
        href="/sign-up"
        style={fontStyle('button')}
        className={`rmbr-lift mt-auto inline-flex h-11 items-center justify-center gap-2 rounded-full transition-colors ${
          featured
            ? 'bg-action text-action-on shadow-[var(--rmbr-glow-action)] hover:bg-action-hover'
            : 'border border-border text-text-primary hover:bg-fill-subtle'
        }`}
      >
        {t('plans.cta')}
        <ArrowRight className="size-3.5" />
      </Link>
    </article>
  );
}
